/* LT-13 Phase 24E — Supplier Enquiry Drawer.
 *
 * Right-side Shadcn Sheet. Loads the aggregated materials dataset, lets the
 * user toggle each material on/off (all checked by default), captures
 * supplier name + quote-by-date + notes, then triggers a PDF or CSV
 * download. Drawer state resets on close (v1 — no persistence per spec OOS).
 *
 * Data shape consumed:
 *   GET /api/tenders/{id}/materials-aggregated
 *     => { tender:{ref,client_name,site_address_lines[]},
 *          categories:[{ name, items:[{id,code,description,unit,qty,rate,cost}], subtotal }],
 *          grand_total, tender_materials_total, reconciliation_delta,
 *          excluded_line_count }
 *
 * a11y: every interactive element has a stable data-testid.
 */
import React, { useEffect, useMemo, useState } from "react";
import {
  ChevronDown, ChevronRight, FileDown, Loader2, Mail, X,
} from "lucide-react";
import { toast } from "sonner";

import { api } from "../lib/api";
import { gbp, ukDate } from "../lib/format";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "./ui/sheet";

const cls = (...a) => a.filter(Boolean).join(" ");


/* ------ Helpers ------------------------------------------------ */
function _filenameFromHeaders(resp, fallback) {
  const x = resp.headers?.["x-pdf-filename"];
  if (x) return x;
  const cd = resp.headers?.["content-disposition"] || "";
  const m = /filename="?([^"]+)"?/i.exec(cd);
  return (m && m[1]) || fallback;
}

function _downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.style.display = "none";
  document.body.appendChild(a); a.click();
  setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 0);
}

/* Today + 14 days, formatted DD/MM/YYYY (UK default for quote-by). */
function _defaultQuoteByDate() {
  const d = new Date(); d.setDate(d.getDate() + 14);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${dd}/${mm}/${d.getFullYear()}`;
}


export default function SupplierEnquiryDrawer({ open, onOpenChange, tenderId, tenderRef }) {
  const [data, setData] = useState(null);            // /materials-aggregated payload
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [supplierName, setSupplierName] = useState("");
  const [quoteByDate, setQuoteByDate] = useState(_defaultQuoteByDate());
  const [notes, setNotes] = useState("");

  // Per-material inclusion state — keyed by stable material id. true = include.
  const [included, setIncluded] = useState({});
  // Per-category expanded state for the collapsible groups.
  const [expanded, setExpanded] = useState({});

  // Per-download loading flags so the two CTAs stay independent.
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [downloadingCsv, setDownloadingCsv] = useState(false);

  /* ----- Reset state when the drawer opens (no persistence, per spec OOS) */
  useEffect(() => {
    if (!open) return;
    setData(null);
    setError(null);
    setSupplierName("");
    setQuoteByDate(_defaultQuoteByDate());
    setNotes("");
    setIncluded({});
    setExpanded({});
    setLoading(true);
    api.get(`/tenders/${tenderId}/materials-aggregated`)
      .then((r) => {
        setData(r.data);
        // Default: all materials checked. All categories expanded.
        const all = {};
        const exp = {};
        for (const cat of r.data.categories || []) {
          exp[cat.name] = true;
          for (const it of cat.items || []) all[it.id] = true;
        }
        setIncluded(all);
        setExpanded(exp);
      })
      .catch(() => setError("Failed to load aggregated materials."))
      .finally(() => setLoading(false));
  }, [open, tenderId]);

  /* ----- Derived counters + selected id list */
  const { totalCount, includedCount, includedIds } = useMemo(() => {
    let tc = 0, ic = 0;
    const ids = [];
    for (const cat of (data?.categories || [])) {
      for (const it of (cat.items || [])) {
        tc += 1;
        if (included[it.id]) { ic += 1; ids.push(it.id); }
      }
    }
    return { totalCount: tc, includedCount: ic, includedIds: ids };
  }, [data, included]);

  /* ----- Toggle helpers */
  const toggleMaterial = (id) => setIncluded((p) => ({ ...p, [id]: !p[id] }));
  const toggleCategory = (cat) => {
    // If any item in the category is unchecked, check all. Otherwise uncheck all.
    const items = cat.items || [];
    const anyUnchecked = items.some((it) => !included[it.id]);
    setIncluded((p) => {
      const next = { ...p };
      for (const it of items) next[it.id] = anyUnchecked;
      return next;
    });
  };
  const toggleExpand = (catName) => setExpanded((p) => ({ ...p, [catName]: !p[catName] }));
  const catChecked = (cat) => (cat.items || []).every((it) => included[it.id]);
  const catPartial = (cat) => {
    const checks = (cat.items || []).filter((it) => included[it.id]).length;
    return checks > 0 && checks < (cat.items || []).length;
  };

  /* ----- Download handlers */
  const handleDownload = async (format) => {
    if (includedCount === 0) {
      toast.error("Toggle at least one material to include in the enquiry.");
      return;
    }
    const setLoading_ = format === "pdf" ? setDownloadingPdf : setDownloadingCsv;
    setLoading_(true);
    const toastId = toast.loading(`Generating supplier enquiry (${format.toUpperCase()})…`);
    try {
      const resp = await api.post(
        `/tenders/${tenderId}/exports/supplier-enquiry`,
        {
          format,
          included_material_ids: includedIds,
          supplier_name: supplierName.trim() || null,
          quote_by_date: quoteByDate.trim() || null,
          notes: notes.trim() || null,
        },
        { responseType: "blob" },
      );
      const filename = _filenameFromHeaders(
        resp, `${tenderRef || "tender"}_supplier_enquiry.${format}`,
      );
      _downloadBlob(resp.data, filename);
      toast.success(`Supplier enquiry ready`, {
        id: toastId,
        description: filename,
        duration: 4000,
      });
    } catch (err) {
      // Parse the blob error body so the toast carries the backend's reason.
      let detail = "Failed to generate supplier enquiry";
      const body = err?.response?.data;
      if (body instanceof Blob) {
        try {
          const txt = await body.text();
          const parsed = JSON.parse(txt);
          if (typeof parsed?.detail === "string") detail = parsed.detail;
        } catch { /* keep generic */ }
      } else if (typeof body?.detail === "string") {
        detail = body.detail;
      }
      toast.error(detail, { id: toastId });
    } finally {
      setLoading_(false);
    }
  };

  /* ----- Render ----- */
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-[640px] sm:max-w-[640px] flex flex-col p-0"
        data-testid="supplier-drawer"
      >
        {/* Header */}
        <SheetHeader className="px-5 py-4 border-b border-line">
          <div className="flex items-start justify-between">
            <div>
              <SheetTitle className="flex items-center gap-2">
                <Mail size={15} className="text-accent" />
                Supplier Enquiry
              </SheetTitle>
              <div className="text-ink-3 mt-0.5" style={{ fontSize: 11.5 }}>
                Generate a request-for-quote for {tenderRef || "this tender"}.
              </div>
            </div>
            <button
              onClick={() => onOpenChange(false)}
              className="text-ink-4 hover:text-ink-2"
              aria-label="Close drawer"
              data-testid="supplier-drawer-close"
            >
              <X size={16} />
            </button>
          </div>
        </SheetHeader>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5" data-testid="supplier-drawer-body">

          {/* Header form (supplier name, quote-by, notes) */}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Supplier name">
              <input
                type="text"
                value={supplierName}
                onChange={(e) => setSupplierName(e.target.value)}
                placeholder="e.g. Travis Perkins"
                className="w-full h-9 px-2.5 rounded-md border border-line bg-surface text-ink focus:outline-none focus:ring-1 focus:ring-accent"
                style={{ fontSize: 13 }}
                data-testid="supplier-name-input"
              />
            </Field>
            <Field label="Quote required by">
              <input
                type="text"
                value={quoteByDate}
                onChange={(e) => setQuoteByDate(e.target.value)}
                placeholder="DD/MM/YYYY"
                className="w-full h-9 px-2.5 rounded-md border border-line bg-surface text-ink font-mono focus:outline-none focus:ring-1 focus:ring-accent"
                style={{ fontSize: 13 }}
                data-testid="supplier-quote-by-input"
              />
            </Field>
            <div className="col-span-2">
              <Field label="Notes (optional)">
                <textarea
                  rows={2}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Confirm UK ply grade, stock availability, etc."
                  className="w-full px-2.5 py-1.5 rounded-md border border-line bg-surface text-ink focus:outline-none focus:ring-1 focus:ring-accent"
                  style={{ fontSize: 13, resize: "vertical" }}
                  data-testid="supplier-notes-input"
                />
              </Field>
            </div>
          </div>

          {/* Project meta — read-only display */}
          {data?.tender && (
            <div className="text-ink-3" style={{ fontSize: 11.5 }}>
              <div><strong className="text-ink-2">Project ref:</strong> {data.tender.ref}</div>
              <div><strong className="text-ink-2">Client:</strong> {data.tender.client_name || "—"}</div>
              <div><strong className="text-ink-2">Site:</strong> {(data.tender.site_address_lines || []).join(", ") || "—"}</div>
            </div>
          )}

          {/* Materials list */}
          <div>
            <div className="col-head mb-2" style={{ fontSize: 10 }}>Materials to include</div>
            {loading ? (
              <div className="flex items-center gap-2 text-ink-3" style={{ fontSize: 12 }}>
                <Loader2 size={13} className="animate-spin" /> Loading aggregated materials…
              </div>
            ) : error ? (
              <div className="text-note-red" style={{ fontSize: 12 }}>{error}</div>
            ) : !data || (data.categories || []).length === 0 ? (
              <div className="text-ink-4 italic" style={{ fontSize: 12 }}>
                No recipe-backed materials in this tender yet.
              </div>
            ) : (
              <div className="space-y-2">
                {data.categories.map((cat) => (
                  <CategoryBlock
                    key={cat.name}
                    cat={cat}
                    expanded={!!expanded[cat.name]}
                    checked={catChecked(cat)}
                    partial={catPartial(cat)}
                    onToggleExpand={() => toggleExpand(cat.name)}
                    onToggleCategory={() => toggleCategory(cat)}
                    onToggleMaterial={toggleMaterial}
                    includedMap={included}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Sticky footer: counter + CTAs */}
        <div className="border-t border-line bg-surface-2/40 px-5 py-3 flex items-center justify-between gap-3">
          <div className="text-ink-2 tabular-nums" style={{ fontSize: 12.5 }} data-testid="supplier-drawer-counter">
            <strong>{includedCount}</strong> of {totalCount} material{totalCount === 1 ? "" : "s"} included
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => handleDownload("csv")}
              disabled={downloadingCsv || downloadingPdf || includedCount === 0}
              className={cls(
                "h-8 px-3 rounded-md border border-line bg-surface flex items-center gap-1.5 text-ink-2 hover:bg-surface-2/60",
                (downloadingCsv || downloadingPdf || includedCount === 0) && "opacity-50 cursor-not-allowed",
              )}
              style={{ fontSize: 12 }}
              data-testid="supplier-drawer-download-csv"
            >
              {downloadingCsv ? <Loader2 size={12} className="animate-spin" /> : <FileDown size={12} />}
              {downloadingCsv ? "Generating…" : "Download CSV"}
            </button>
            <button
              type="button"
              onClick={() => handleDownload("pdf")}
              disabled={downloadingCsv || downloadingPdf || includedCount === 0}
              className={cls(
                "h-8 px-3 rounded-md text-white shadow-soft flex items-center gap-1.5",
                (downloadingCsv || downloadingPdf || includedCount === 0) && "opacity-50 cursor-not-allowed",
              )}
              style={{ fontSize: 12, fontWeight: 500, background: "#F4A340" }}
              data-testid="supplier-drawer-download-pdf"
            >
              {downloadingPdf ? <Loader2 size={12} className="animate-spin" /> : <FileDown size={12} />}
              {downloadingPdf ? "Generating…" : "Download PDF"}
            </button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}


/* ---------- Sub-components ---------- */
function Field({ label, children }) {
  return (
    <label className="block">
      <div className="col-head mb-1" style={{ fontSize: 10 }}>{label}</div>
      {children}
    </label>
  );
}


function CategoryBlock({
  cat, expanded, checked, partial, onToggleExpand, onToggleCategory,
  onToggleMaterial, includedMap,
}) {
  return (
    <div className="rounded-md border border-line bg-surface overflow-hidden">
      <div className="flex items-center gap-2 px-3 py-2 bg-surface-2/30">
        <button
          type="button"
          onClick={onToggleExpand}
          className="text-ink-3 hover:text-ink-2"
          data-testid={`supplier-category-expand-${cat.name}`}
          aria-label={expanded ? "Collapse" : "Expand"}
        >
          {expanded ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
        </button>
        <input
          type="checkbox"
          checked={checked}
          ref={(el) => { if (el) el.indeterminate = !checked && partial; }}
          onChange={onToggleCategory}
          data-testid={`supplier-category-toggle-${cat.name}`}
          aria-label={`Toggle all in ${cat.name}`}
        />
        <span className="text-ink" style={{ fontSize: 12.5, fontWeight: 600 }}>{cat.name}</span>
        <span className="text-ink-4 ml-auto" style={{ fontSize: 11 }}>
          {cat.items.length} item{cat.items.length === 1 ? "" : "s"} · {gbp(cat.subtotal)}
        </span>
      </div>
      {expanded && (
        <ul className="divide-y divide-line/60">
          {cat.items.map((it) => (
            <li key={it.id} className="flex items-center gap-3 px-3 py-2">
              <input
                type="checkbox"
                checked={!!includedMap[it.id]}
                onChange={() => onToggleMaterial(it.id)}
                data-testid={`supplier-material-toggle-${it.id}`}
                aria-label={`Toggle ${it.description}`}
              />
              <div className="flex-1 min-w-0">
                <div className="text-ink truncate" style={{ fontSize: 12.5 }}>{it.description}</div>
                <div className="text-ink-4 truncate" style={{ fontSize: 11 }}>
                  {it.code ? <span className="font-mono mr-2">{it.code}</span> : null}
                  Qty {it.qty} {it.unit || ""} · {gbp(it.cost)}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
